/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AdobeAdvertisingCloud from '@react/react-spectrum/Icon/AdobeAdvertisingCloud';
import AdobeAnalytics from '@react/react-spectrum/Icon/AdobeAnalytics';
import AdobeAnalyticsColor from '@react/react-spectrum/Icon/AdobeAnalyticsColorDark';
import AdobeAudienceManager from '@react/react-spectrum/Icon/AdobeAudienceManager';
import AdobeAudienceManagerColor from '@react/react-spectrum/Icon/AdobeAudienceManagerColorDark';
import AdobeCampaign from '@react/react-spectrum/Icon/AdobeCampaign';
import AdobeCampaignColor from '@react/react-spectrum/Icon/AdobeCampaignColorDark';
import AdobeExperienceCloud from '@react/react-spectrum/Icon/AdobeExperienceCloud';
import AdobeExperienceManager from '@react/react-spectrum/Icon/AdobeExperienceManager';
import AdobeExperienceManagerColor from '@react/react-spectrum/Icon/AdobeExperienceManagerColorDark';
import AdobePrimetime from '@react/react-spectrum/Icon/AdobePrimetime';
import AdobePrimetimeColor from '@react/react-spectrum/Icon/AdobePrimetimeColorDark';
import AdobeTarget from '@react/react-spectrum/Icon/AdobeTarget';
import AdobeTargetColor from '@react/react-spectrum/Icon/AdobeTargetColorDark';
import Icon from '@react/react-spectrum/Icon';
import PropTypes from 'prop-types';
import React from 'react';

const Icons = {
  AdobeAdvertisingCloud,
  AdobeAnalytics,
  AdobeAnalyticsColor,
  AdobeAudienceManager,
  AdobeAudienceManagerColor,
  AdobeCampaign,
  AdobeCampaignColor,
  AdobeExperienceCloud,
  AdobeExperienceManager,
  AdobeExperienceManagerColor,
  AdobePrimetime,
  AdobePrimetimeColor,
  AdobeTarget,
  AdobeTargetColor
};

/* eslint-disable max-len */
const TempIcons = {
  AdobeAdvertisingCloudColor: (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient
          cx="10.342"
          cy="-1.768"
          gradientTransform="translate(-0.491 -0.157) scale(0.952 0.977)"
          gradientUnits="userSpaceOnUse"
          id="radial-gradient"
          r="40.281"
        >
          <stop offset="0" stopColor="#ec202b" />
          <stop offset="0.146" stopColor="#e2202a" />
          <stop offset="0.399" stopColor="#c51e26" />
          <stop offset="0.726" stopColor="#981c20" />
          <stop offset="1" stopColor="#6d1a1b" />
        </radialGradient>
      </defs>
      <rect fill="url(#radial-gradient)" height="24" width="24" x="-0.007" y="-0.037" />
      <path
        d="M21.993,11.857C21.993,6.983,18.508,3,14.225,3a8.384,8.384,0,0,0-8.1,8.168H2.09c0,4.875,3.62,9.8,7.9,9.8,4.161,0,7.755-4.461,7.945-9.109Zm-7.967-.694c.873-2.141,1.895-2.795,3.388-2.795a2.841,2.841,0,0,1,2.924,2.8Zm.239-7.346a7.155,7.155,0,0,1,6.946,5.73,4.189,4.189,0,0,0-3.8-2.433c-2.714,0-3.95,1.954-4.734,4.049H6.9A7.388,7.388,0,0,1,14.265,3.817Zm-1.72,8.04c-.618,1.43-2.161,4.218-4.794,4.218-2.341,0-3.944-1.681-4.069-4.218ZM9.9,20.107c-2.843,0-5.278-2.273-6.471-5.01a5.059,5.059,0,0,0,4.327,2.232c2.551,0,4.845-2.044,6.147-5.472h3.283C16.993,16.228,13.78,20.107,9.9,20.107Z"
        fill="#f9f7f7"
      />
    </svg>
  ),
  AdobeExperienceCloudColor: (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient
          cx="10.342"
          cy="1.789"
          gradientTransform="translate(-0.491 -3.631) scale(0.952 0.977)"
          gradientUnits="userSpaceOnUse"
          id="radial-gradient"
          r="40.281"
        >
          <stop offset="0" stopColor="#ec202b" />
          <stop offset="0.146" stopColor="#e2202a" />
          <stop offset="0.399" stopColor="#c51e26" />
          <stop offset="0.726" stopColor="#981c20" />
          <stop offset="1" stopColor="#6d1a1b" />
        </radialGradient>
      </defs>
      <rect fill="url(#radial-gradient)" height="24" width="24" x="-0.007" y="-0.037" />
      <path
        d="M18.64,8.162a22.126,22.126,0,0,1,.742-3.109A8.854,8.854,0,0,0,18.244,4a23.833,23.833,0,0,0-1.075,4.5l-.013.09A23.82,23.82,0,0,1,12.4,5.769a24.4,24.4,0,0,0,3.39-3.211c-.128-.052-.433-.377-.561-.428a22.05,22.05,0,0,1-3.233,3.033A35.136,35.136,0,0,1,8.959,2c-.128.039-.426.421-.554.472a23.467,23.467,0,0,0,3.377,3.3,23.643,23.643,0,0,1-4.81,2.764l-.013-.09A24.749,24.749,0,0,0,5.923,3.837,9.628,9.628,0,0,0,4.771,4.861,23.383,23.383,0,0,1,5.5,8.085a23,23,0,0,1-3.173.934,9.809,9.809,0,0,0-.32,1.5,23.254,23.254,0,0,0,4.53-1.344l.089-.038a23.5,23.5,0,0,1-.038,5.527A23.738,23.738,0,0,0,2.02,13.305c.026.141.014.651.039.791a20.7,20.7,0,0,1,4.1,1.234,19.424,19.424,0,0,1-1,4.066c.1.09.482.319.6.4A22.63,22.63,0,0,0,6.9,15.211,24.165,24.165,0,0,1,11.667,18l-.089.064A24.725,24.725,0,0,0,8.162,21.2a9.813,9.813,0,0,0,1.471.474,23.664,23.664,0,0,1,2.354-2.163,23,23,0,0,1,2.3,2.163,9.4,9.4,0,0,0,1.472-.474,25.007,25.007,0,0,0-3.327-3.147l-.09-.064a23.842,23.842,0,0,1,4.824-2.725,23.672,23.672,0,0,0,1.062,4.491l.562-.491a19.348,19.348,0,0,1-1.034-3.933,19.871,19.871,0,0,1,3.967-1.067c.026-.128.19-.676.215-.817A24.083,24.083,0,0,0,17.5,14.712a24.213,24.213,0,0,1,.025-5.527l.09.039a24.847,24.847,0,0,0,4.376,1.369,9.95,9.95,0,0,0-.307-1.51A17.245,17.245,0,0,1,18.64,8.162Zm-2.059,6.429a21.483,21.483,0,0,0-4.568,2.578l-.023-.024-.023.035a21.1,21.1,0,0,0-4.534-2.647A20.987,20.987,0,0,0,7.468,9.3l.046-.012L7.5,9.24a21.558,21.558,0,0,0,4.557-2.612A21.283,21.283,0,0,0,16.57,9.309l-.012.046.035.012A22,22,0,0,0,16.581,14.591Z"
        fill="#f9f7f7"
      />
    </svg>
  ),
  AdobeExperiencePlatform: (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.4,6v12l-4.8-2.8c-1.7-1-3.1-2.4-4.1-4.1L22.4,6z" fill="#909090" />
      <path d="M22.4,6l-8.9,5.1c-1-1.7-1.5-3.6-1.5-5.6V0L22.4,6z" fill="#4A4A4A" />
      <path d="M12,0v5.6c0,2-0.5,3.9-1.5,5.6L1.6,6L12,0z" fill="#6E6E6E" />
      <path d="M10.5,11.1c-1,1.7-2.4,3.1-4.1,4.1l0,0L1.6,18V6L10.5,11.1z" fill="#909090" />
      <path d="M22.4,18L12,24V13.7c2,0,3.9,0.5,5.6,1.5L22.4,18z" fill="#6E6E6E" />
      <path d="M12,13.7V24L1.6,18l4.8-2.8C8.1,14.2,10,13.7,12,13.7z" fill="#4A4A4A" />
    </svg>
  ),
  AdobeExperiencePlatformColor: (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.3922,6V18L17.568,15.21559a11.13343,11.13343,0,0,1-4.07637-4.078Z" fill="#f90025" />
      <path d="M22.3922,6l-8.90055,5.13763A11.118,11.118,0,0,1,11.9992,5.56881V0Z" fill="#8d0017" />
      <path d="M11.9992,0V5.57041a11.11212,11.11212,0,0,1-1.49244,5.56722L1.6078,6Z" fill="#c20020" />
      <path d="M10.50676,11.13763a11.13507,11.13507,0,0,1-4.06842,4.07319l-.008.00477L1.6078,18V6Z" fill="#f90025" />
      <path d="M22.3922,18l-10.393,6V13.72315A11.11661,11.11661,0,0,1,17.568,15.21559Z" fill="#c20020" />
      <path
        d="M11.9992,13.72315V24L1.6078,18l4.82259-2.78441A11.11655,11.11655,0,0,1,11.9992,13.72315Z"
        fill="#8d0017"
      />
    </svg>
  ),
  MagentoCommerce: (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12,1.2c1.5,0,2.9,0.3,4.2,0.8c1.3,0.5,2.4,1.3,3.4,2.3s1.8,2.1,2.3,3.4c0.6,1.3,0.8,2.7,0.8,4.2s-0.3,2.9-0.8,4.2s-1.3,2.4-2.3,3.4s-2.1,1.8-3.4,2.3c-1.3,0.6-2.7,0.8-4.2,0.8s-2.9-0.3-4.2-0.8s-2.4-1.3-3.4-2.3s-1.8-2.1-2.3-3.4c-0.6-1.3-0.8-2.7-0.8-4.2S1.6,9,2.1,7.7s1.3-2.4,2.3-3.4S6.5,2.5,7.8,2S10.5,1.2,12,1.2 M12,0C5.4,0,0,5.4,0,12s5.4,12,12,12s12-5.4,12-12S18.6,0,12,0L12,0z"
        fill="#7C7C7C"
      />
      <polygon
        fill="#7C7C7C"
        points="12,4.1 5.2,8.1 5.2,15.9 7.1,17 7.1,9.2 12,6.4 16.9,9.2 16.9,17 18.8,15.9 18.8,8.1"
      />
      <polygon
        fill="#7C7C7C"
        points="13,17 12,17.6 11,17.1 11,9.2 9.1,10.3 9.1,18.2 12,19.9 14.9,18.2 14.9,10.3 13,9.2"
      />
    </svg>
  ),
  MagentoCommerceColor: (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12,1.2c1.5,0,2.9,0.3,4.2,0.8c1.3,0.5,2.4,1.3,3.4,2.3c1,1,1.8,2.1,2.3,3.4c0.6,1.3,0.8,2.7,0.8,4.2s-0.3,2.9-0.8,4.2c-0.5,1.3-1.3,2.4-2.3,3.4c-1,1-2.1,1.8-3.4,2.3c-1.3,0.6-2.7,0.8-4.2,0.8s-2.9-0.3-4.2-0.8c-1.3-0.5-2.4-1.3-3.4-2.3c-1-1-1.8-2.1-2.3-3.4c-0.6-1.3-0.8-2.7-0.8-4.2s0.3-2.9,0.8-4.2c0.5-1.3,1.3-2.4,2.3-3.4c1-1,2.1-1.8,3.4-2.3C9.1,1.5,10.5,1.2,12,1.2 M12,0C5.4,0,0,5.4,0,12s5.4,12,12,12s12-5.4,12-12S18.6,0,12,0L12,0z"
        fill="#FF661A"
      />
      <polygon
        fill="#FFDFDD"
        points="12,4.1 5.2,8.1 5.2,15.9 7.1,17 7.1,9.2 12,6.4 16.9,9.2 16.9,17 18.8,15.9 18.8,8.1"
      />
      <polygon
        fill="#FFDFDD"
        points="13,17 12,17.6 11,17.1 11,9.2 9.1,10.3 9.1,18.2 12,19.9 14.9,18.2 14.9,10.3 13,9.2"
      />
    </svg>
  )
};
/* eslint-enable max-len */

const ColorIcon = ({enabled, iconName, iconSize}) => {
  let IconComponent = AdobeExperienceCloud;

  if (iconName in Icons) {
    if (iconName === 'AdobeExperienceCloud') {
      return <Icon size={iconSize}>{TempIcons.AdobeExperienceCloudColor}</Icon>;
    } else if (iconName === 'AdobeMediaOptimizer' || iconName === 'AdobeAdvertisingCloud') {
      return enabled ? <Icon size={iconSize}>{TempIcons.AdobeAdvertisingCloudColor}</Icon> : <AdobeAdvertisingCloud size={iconSize} />;
    }
    IconComponent = Icons[enabled ? `${iconName}Color` : iconName];
    return <IconComponent size={iconSize} />;
  } else if (iconName in TempIcons) {
    return <Icon size={iconSize}>{TempIcons[enabled ? `${iconName}Color` : iconName]}</Icon>;
  }
  return <IconComponent size={iconSize} />;
};

ColorIcon.propTypes = {
  enabled: PropTypes.bool,
  iconName: PropTypes.string,
  iconSize: PropTypes.string
};

ColorIcon.defaultProps = {
  enabled: true,
  iconName: 'AdobeExperienceCloud',
  iconSize: 'XL'
};

export default React.memo(ColorIcon);
